import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Seo } from "../components/Seo";

const CATS = [
  { slug: "all", name: "All" },
  { slug: "nuts", name: "Nuts" },
  { slug: "dry-fruits", name: "Dry Fruits" },
  { slug: "seeds", name: "Seeds" },
  { slug: "berries", name: "Berries" },
  { slug: "gift-boxes", name: "Gift Boxes" },
  { slug: "exotic-collection", name: "Exotic" },
  { slug: "corporate-gifts", name: "Corporate" },
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const search = params.get("search") || "";
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category !== "all") q.category = category;
    if (search) q.search = search;
    if (sort !== "featured") q.sort = sort;
    api.get("/products", { params: q }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, sort, search]);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 fade-in">
      <Seo title={search ? `Search: ${search}` : "Shop All Dry Fruits & Nuts"} description="Browse our full collection of premium nuts, dry fruits, seeds, berries and gift boxes. Delivered fresh across Sitapur." />
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-gold">The Collection</span>
        <h1 className="font-serif text-5xl text-forest mt-1">{search ? `Results for "${search}"` : "Shop All"}</h1>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 border-b border-border pb-5">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => { setCategory(c.slug); if (search) setParams({}); }}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${category === c.slug && !search ? "bg-forest text-cream" : "bg-sand text-forest hover:bg-gold/20"}`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44 rounded-full border-border" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="featured">Featured</SelectItem>
            <SelectItem value="price_low">Price: Low to High</SelectItem>
            <SelectItem value="price_high">Price: High to Low</SelectItem>
            <SelectItem value="rating">Top Rated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[...Array(8)].map((_, i) => <div key={i}><Skeleton className="aspect-square rounded-xl mb-4" /><Skeleton className="h-4 w-2/3 mb-2" /><Skeleton className="h-3 w-1/2" /></div>)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground" data-testid="no-products">No products found. Try another category.</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="products-grid">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
